// 체크리스트 평가 기준(기준/관리/현장 등) 섹션의 단일 출처.
//
// 설문 진행 패널·탐색 상세·인증 결과 상세 세 화면이 각자 정규식으로 지침을 파싱하다 드리프트했다
// (구조화 컬럼 사용 여부·헤딩 캡처·폴백이 제각각). 여기 한 곳으로 통일한다.
//
// 우선순위: ① 구조화 컬럼(분류 인지) → ② guidance 정규식 파싱 → ③ scoringScale.
// 구조화 컬럼 내용이 자기 라벨("기준 (1점)")로 시작하면 그 줄을 제목으로 승격해 중복 표기를 없앤다.

export type ChecklistCriteriaSection = { title: string; content: string }

/** 헬퍼가 읽는 최소 형태(ChecklistItem·CatalogChecklistItem 등이 모두 만족). */
export interface ChecklistCriteriaSource {
    classification?: string | null
    scoringScale?: string | null
    guidance?: string | null
    /** 선행요건 구조화 컬럼. */
    criteriaStandard?: string | null
    criteriaManagement?: string | null
    criteriaOnsite?: string | null
    /** HACCP관리 세부항목. */
    haccpDetail?: string | null
    /** 공통 참고. */
    inspectorNotes?: string | null
    referenceNotes?: string | null
}

// 채점단위 헤더 라인("기준 (1점)", "기준(1) (1점)", "관리 · 현장 (1점)" 등). 한 컬럼에
// 이 헤더가 여러 개면(0~4점의 기준(1)/기준(2)) 헤더마다 별도 카드로 쪼갠다.
const SCORE_HEADER_RE = /\(\s*\d+\s*점\s*\)\s*$/
// 통합 카드(0~1·0~2점) 안의 (기준)/(관리)/(현장) 소제목 — 굵게+줄바꿈으로 구분한다.
const SUB_LABEL_RE = /^\((기준|관리|현장)\)$/

/**
 * (기준)/(관리)/(현장) 소제목을 빈 줄로 띄워 한 줄로 뭉치지 않게 한다.
 * 마크다운 렌더(찾아보기)는 빈 줄로 단락이 분리되고, whitespace-pre-line 렌더
 * (설문 진행·결과 상세)도 빈 줄이 그대로 보여, 세 화면 모두에서 안전하다.
 * 마크다운 문법(굵게 등)은 pre-line 화면에서 기호가 그대로 노출되므로 쓰지 않는다.
 */
function emphasizeSubLabels(content: string): string {
    return content
        .split('\n')
        .map((line) => {
            const match = SUB_LABEL_RE.exec(line.trim())
            return match ? `\n(${match[1]})\n` : line
        })
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

/**
 * 구조화 컬럼 한 개 → 점수 카드 섹션들.
 * 컬럼 안에 채점단위 헤더가 여럿이면 카드로 분리하고(예: 기준(1)/기준(2)), 없으면
 * 라벨을 제목으로 한 카드 1장으로 둔다. 통합 카드의 소제목은 줄바꿈해 가독성을 살린다.
 */
function structuredSections(
    label: string,
    raw?: string | null
): ChecklistCriteriaSection[] {
    const value = raw?.trim()
    if (!value) return []

    const lines = value.split('\n')
    const headerIdx = lines
        .map((line, i) => (SCORE_HEADER_RE.test(line.trim()) ? i : -1))
        .filter((i) => i >= 0)

    // 헤더 없음: 라벨을 제목으로. 첫 줄이 라벨 자체면 중복 제거.
    if (headerIdx.length === 0) {
        const body = lines[0].trim() === label ? lines.slice(1) : lines
        return [{ title: label, content: emphasizeSubLabels(body.join('\n')) }]
    }

    const sections: ChecklistCriteriaSection[] = []
    // 첫 헤더 앞의 서두 텍스트(드묾)는 라벨 카드로 선행한다.
    if (headerIdx[0] > 0) {
        const pre = lines.slice(0, headerIdx[0]).join('\n').trim()
        if (pre) sections.push({ title: label, content: emphasizeSubLabels(pre) })
    }
    headerIdx.forEach((start, k) => {
        const end = headerIdx[k + 1] ?? lines.length
        const title = lines[start].trim()
        const content = emphasizeSubLabels(lines.slice(start + 1, end).join('\n'))
        sections.push({ title, content })
    })
    return sections
}

/** guidance 텍스트를 기준/관리/현장/확인사항 헤딩으로 분리(구조화 컬럼이 없을 때 폴백). */
function parseGuidance(guidance?: string | null): ChecklistCriteriaSection[] {
    const source = guidance?.trim()
    if (!source) return []

    const sections: ChecklistCriteriaSection[] = []
    const headings = [
        ...source.matchAll(
            /(?:^|\n)\s*(기준|관리|현장|확인사항)\s*(\([^)\n]+\))?\s*\n/g
        ),
    ]
    headings.forEach((match, index) => {
        const title = [match[1], match[2]].filter(Boolean).join(' ')
        const start = (match.index ?? 0) + match[0].length
        const end = headings[index + 1]?.index ?? source.length
        const content = source.slice(start, end).trim()
        if (title && content) sections.push({ title, content })
    })
    if (sections.length > 0) return sections

    // 레거시: 빈 줄 구분 관리/현장/확인사항 블록.
    const legacy =
        /(?:^|\n\n)(관리|현장|확인사항)\n([\s\S]*?)(?=\n\n(?:관리|현장|확인사항)\n|$)/g
    for (const match of source.matchAll(legacy)) {
        const title = match[1]?.trim()
        const content = match[2]?.trim()
        if (title && content) sections.push({ title, content })
    }
    return sections.length > 0
        ? sections
        : [{ title: '세부 내용', content: source }]
}

/**
 * 체크리스트 항목의 평가 기준 섹션(단일 출처).
 * 구조화 컬럼 우선 → guidance 파싱 → scoringScale. 아무것도 없으면 안내 문구 1개.
 */
export function checklistCriteriaSections(
    item: ChecklistCriteriaSource
): ChecklistCriteriaSection[] {
    const structured: ChecklistCriteriaSection[] = []
    const add = (label: string, raw?: string | null) => {
        structured.push(...structuredSections(label, raw))
    }
    if (item.classification === '선행요건') {
        add('기준', item.criteriaStandard)
        add('관리', item.criteriaManagement)
        add('현장', item.criteriaOnsite)
    } else {
        add('세부항목', item.haccpDetail)
    }
    add('지도관 고려사항', item.inspectorNotes)
    add('기타', item.referenceNotes)
    if (structured.length > 0) return structured

    const parsed = parseGuidance(item.guidance)
    const hasRubric = parsed.some((s) =>
        /^(기준|관리|현장)(\s|\(|$)/.test(s.title)
    )
    if (hasRubric) return parsed

    const result: ChecklistCriteriaSection[] = []
    const scale = item.scoringScale?.trim()
    if (scale) result.push({ title: '기준', content: scale })
    result.push(...parsed)
    return result.length > 0
        ? result
        : [{ title: '기준', content: '등록된 기준 정보가 없습니다.' }]
}
